/**
 * residentWellbeingDisplay — listing-card helpers for the wellbeing strip
 * (latest mood / meal / fluids from card_summary.wellbeing), the discrete
 * attention badges (AI alerts, overdue observations / evaluations, missed
 * rounds) and the card's RAG accent.
 *
 * `card_summary.wellbeing` only exists once the API ships it — key absence
 * means "old API", and the strip must not render at all (buildWellbeingSignals
 * returns null rather than []).
 */

import { MOODS, MOOD_ORDER, moodBadgeTone, moodGroup } from "./moodScale";

// Label / emoji / tone per mood, from the ONE mood scale (moodScale.js, which
// mirrors App\Support\MoodScale): the tone comes from the mood's GROUP, so
// anxious is danger (with distressed) and flat is neutral here as everywhere.
export const MOOD_META = Object.fromEntries(
  MOOD_ORDER.map((value) => [
    value,
    {
      emoji: MOODS[value].emoji,
      label: MOODS[value].label,
      tone: moodBadgeTone(value),
    },
  ]),
);

/** Catalog "Amount eaten" options → reading-friendly fractions. */
const CONSUMED_LABELS = {
  0: "Nothing",
  25: "¼",
  50: "½",
  75: "¾",
  100: "All",
};

const capitalize = (value) => {
  const str = String(value ?? "").trim();
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const startOfDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const formatWellbeingRecency = (value) => {
  if (!value) return null;
  const date = new Date(
    typeof value === "string" ? value.replace(" ", "T") : value,
  );
  if (Number.isNaN(date.getTime())) return null;
  const days = Math.round(
    (startOfDay(new Date()) - startOfDay(date)) / 86400000,
  );
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  return `${days}d ago`;
};

/** Whole days between a recorded timestamp and now (null when unparseable). */
const daysSince = (value) => {
  if (!value) return null;
  const date = new Date(
    typeof value === "string" ? value.replace(" ", "T") : value,
  );
  if (Number.isNaN(date.getTime())) return null;
  return Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
};

/** Mood older than this reads as history, not a live signal — drop its tone. */
const STALE_MOOD_DAYS = 7;

/** Recorded fluids below this (with drinks logged) read as a soft low-intake prompt. */
const LOW_FLUID_ML = 500;

/** Meal consumed at/below this fraction (%) is poor intake worth surfacing. */
const POOR_INTAKE_PCT = 25;

/** Care-day rhythm: from this hour we expect a check-in logged *today* (before
 *  it, and overnight, the most recent check-in carries over). Tune to taste. */
const CHECKIN_EXPECTED_FROM_HOUR = 11;
/** From this hour residents are settling for the night — stop expecting fresh
 *  daytime check-ins and carry the day's over. */
const NIGHT_FROM_HOUR = 22;

const formatMealType = (mealType) => {
  if (!mealType) return null;
  return capitalize(String(mealType).replace(/_/g, " "));
};

/**
 * Wellbeing strip items: [{ key, emoji, label, detail, tone, title }].
 * Returns null when the API payload has no `wellbeing` key (strip hidden);
 * returns [] when there's no recent mood and no intake concern (empty state) —
 * intake is exception-only, so good eating/hydration deliberately adds nothing.
 */
export const buildWellbeingSignals = (cardSummary) => {
  const wellbeing = cardSummary?.wellbeing;
  if (!wellbeing || typeof wellbeing !== "object") return null;

  const signals = [];

  const mood = wellbeing.mood;
  if (mood?.value) {
    const meta = MOOD_META[mood.value] ?? {};
    // Tone from the mood's group — the API's `group` when it sends one.
    const groupTone = moodBadgeTone(mood.value, mood.group);
    // Label/emoji prefer the API payload (catalog source of truth); the local
    // map is the tone source + fallback for older payloads.
    const emoji = mood.emoji ?? meta.emoji ?? "🙂";
    const label = mood.label ?? meta.label ?? capitalize(mood.value);
    const recency = formatWellbeingRecency(mood.recorded_at);
    // Mood can be up to 30 days old; a stale "Distressed" must not read as red-
    // urgent as one recorded today. Past the freshness window, drop the tone so
    // only recent moods carry colour (the recency string still shows the age).
    const moodAge = daysSince(mood.recorded_at);
    const tone =
      moodAge != null && moodAge > STALE_MOOD_DAYS ? "neutral" : groupTone;
    signals.push({
      key: "mood",
      emoji,
      label,
      detail: recency,
      tone,
      title: `Latest mood — ${label}${recency ? ` · ${recency}` : ""}`,
    });
  }

  // Intake is exception-only: a well-eaten meal / normal fluids is reassuring
  // but near-constant across the grid, so only poor intake earns a chip. Full
  // detail lives in the resident's nutrition view.
  const nutrition = wellbeing.nutrition;
  const consumed =
    nutrition && nutrition.last_meal_consumed != null
      ? Number(nutrition.last_meal_consumed)
      : null;
  if (consumed != null && consumed <= POOR_INTAKE_PCT) {
    const fraction = CONSUMED_LABELS[consumed] ?? `${consumed}%`;
    const mealLabel = formatMealType(nutrition.last_meal_type);
    const recency = formatWellbeingRecency(nutrition.last_meal_at);
    // Meal record Option B (29 Sep 2026): a meal's 0 is "Offered — declined"
    // (or an older "None") — the meal chart calls it Declined, and so does this.
    const declined = consumed === 0;
    signals.push({
      key: "nutrition",
      emoji: "🍽️",
      label: declined ? "Declined" : `${fraction} eaten`,
      detail: [mealLabel, recency].filter(Boolean).join(" · ") || null,
      tone: declined ? "danger" : "warning",
      title: `Last meal${mealLabel ? ` — ${mealLabel}` : ""} · ${
        declined ? "declined" : `${fraction} eaten`
      }${recency ? ` · ${recency}` : ""}`,
    });
  }

  const hydration = wellbeing.hydration;
  const ml = Number(hydration?.total_ml_today) || 0;
  const drinks = Number(hydration?.drinks_today) || 0;
  // Only flag low recorded fluids (volumes accrue through the day, so this is a
  // soft prompt, not an alarm); well-hydrated residents add no chip.
  if (drinks > 0 && ml > 0 && ml < LOW_FLUID_ML) {
    signals.push({
      key: "hydration",
      emoji: "💧",
      label: `${ml}ml`,
      detail: "today",
      tone: "warning",
      title: `Fluids today — ${ml}ml across ${drinks} drink${
        drinks === 1 ? "" : "s"
      }`,
    });
  }

  return signals;
};

const pluralize = (count, singular, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

// A one-flag chip shows its ask, cut to fit beside the other chips.
const FLAG_ASK_CHARS = 30;

const truncate = (text, max) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

/**
 * Discrete attention badges replacing the truncated headline chip.
 * Each item carries the resident-edit tab slug it deep-links to.
 */
export const buildAttentionItems = (cardSummary) => {
  if (!cardSummary || typeof cardSummary !== "object") return [];
  const items = [];

  // Staff flags — a named person asking the team (raised from Charts). Their
  // own chip, never counted into "alerts": one flag shows its ask, several
  // show a count; red while any is urgent. Links to the Flags page
  // (`page: "flags"`), not a resident-edit tab. Absent on older payloads.
  const flags = cardSummary.staff_flags ?? {};
  const flagCount = Number(flags.open_count) || 0;
  if (flagCount > 0) {
    const top = Array.isArray(flags.top) ? flags.top : [];
    const urgent = (Number(flags.urgent_count) || 0) > 0;
    const ask = String(top[0]?.ask ?? "").trim();
    items.push({
      key: "flags",
      tone: urgent ? "danger" : "flag",
      icon: "flag",
      label:
        flagCount === 1 && ask
          ? `Flag: ${truncate(ask, FLAG_ASK_CHARS)}`
          : pluralize(flagCount, "flag"),
      sub: urgent && flagCount > 1 ? "urgent" : null,
      tab: "care-plan",
      page: "flags",
      title:
        top
          .map((f) =>
            [f.ask, f.raised_by_name ? `from ${f.raised_by_name}` : null]
              .filter(Boolean)
              .join(" — "),
          )
          .filter(Boolean)
          .join("\n") || null,
    });
  }

  // Onboarding setup — newly admitted residents with assessments / care plans
  // still to complete. Prepended so the setup work reads first on the card.
  // `card_summary.onboarding` only exists once the API ships it; absence means
  // "old API" and the chip simply never renders. Gated on is_new (matching the
  // API's headline/urgency gating) — an established resident can have open
  // pathway queue items, and the chip's copy claims "Newly admitted".
  const onboarding = cardSummary.onboarding;
  const pendingAssessments = Number(onboarding?.pending_assessments) || 0;
  const pendingCarePlans = Number(onboarding?.pending_care_plans) || 0;
  if (onboarding?.is_new && pendingAssessments + pendingCarePlans > 0) {
    const parts = [];
    if (pendingAssessments > 0) {
      parts.push(pluralize(pendingAssessments, "assessment"));
    }
    if (pendingCarePlans > 0) {
      parts.push(pluralize(pendingCarePlans, "care plan"));
    }
    items.push({
      key: "onboarding",
      tone: "pending",
      label: `Setup: ${parts.join(" · ")}`,
      sub: null,
      // Assessments have no tab of their own — they live in the Care Plan
      // workspace alongside the care plans this chip also counts.
      tab: "care-plan",
      title: "Newly admitted — complete assessments and care plans",
    });
  }

  const alerts = cardSummary.ai_alerts ?? {};
  const openCount = alerts.open_count ?? alerts.count ?? 0;
  if (openCount > 0) {
    // by_priority spans open+monitoring items; the open-only split (newer
    // payloads) keeps the sub-label from exceeding the headline count.
    const high =
      alerts.open_by_priority?.high ??
      Math.min(alerts.by_priority?.high ?? 0, openCount);
    items.push({
      key: "alerts",
      tone: high > 0 ? "danger" : "warning",
      label: pluralize(openCount, "alert"),
      sub: high > 0 ? `${high} high` : null,
      // AI Alerts tab retired — alerts live on the Care Plan tab (Overview home
      // + the workspace "Alerts & anomalies" detail).
      tab: "care-plan",
      title: alerts.open_top_title ?? alerts.top_item?.title ?? null,
    });
  }

  const observations = cardSummary.observations ?? {};
  if (observations.overdue_count > 0) {
    items.push({
      key: "observations",
      tone: "danger",
      // Lead with the schedule name when exactly one is overdue — a manager can
      // then tell a repositioning check from a vitals round without hovering.
      label:
        observations.overdue_count === 1 && observations.next_title
          ? `${observations.next_title} overdue`
          : `${observations.overdue_count} obs overdue`,
      sub: null,
      tab: "observations",
      title: observations.next_title ?? null,
    });
  } else if (observations.due_soon_count > 0) {
    items.push({
      key: "observations",
      tone: "pending",
      label: `${observations.due_soon_count} obs due soon`,
      sub: null,
      tab: "observations",
      title: observations.next_title ?? null,
    });
  }

  const evaluations = cardSummary.evaluations ?? {};
  if (evaluations.overdue_count > 0) {
    // overdue_days occasionally arrives as a junk magnitude — only annotate
    // when it reads like a real backlog.
    const overdueDays =
      evaluations.overdue_days > 0 && evaluations.overdue_days <= 365
        ? evaluations.overdue_days
        : null;
    items.push({
      key: "evaluations",
      tone: "danger",
      // Name the care plan when a single evaluation is overdue; fall back to the
      // generic form for multiples or when the label is missing.
      label:
        evaluations.overdue_count === 1
          ? `${
              evaluations.nearest_due_label
                ? `${evaluations.nearest_due_label} overdue`
                : "Review overdue"
            }${overdueDays ? ` · ${overdueDays}d` : ""}`
          : `${evaluations.overdue_count} reviews overdue`,
      sub: null,
      tab: "care-plan",
      title: evaluations.nearest_due_label ?? null,
    });
  } else if (evaluations.due_soon_count > 0) {
    items.push({
      key: "evaluations",
      tone: "pending",
      label: "Review due soon",
      sub: null,
      tab: "care-plan",
      title: evaluations.nearest_due_label ?? null,
    });
  }

  const round = cardSummary.round_summary ?? {};
  if (round.missed > 0) {
    items.push({
      key: "round",
      tone: "warning",
      // Prefer the server's labelled string ("2 missed in Morning Round") when
      // it actually describes the misses; else the bare count.
      label:
        typeof round.label === "string" && /miss/i.test(round.label)
          ? round.label
          : `${round.missed} round${round.missed === 1 ? "" : "s"} missed`,
      sub: null,
      tab: "observations",
      title: round.active_round_name ?? null,
    });
  }

  // Clinical tool risk (Waterlow / MUST / etc.) — surfaced only when high or
  // declining, so most cards stay unchanged. Barthel has its own ADL badge.
  const clinicalScores = Array.isArray(cardSummary.clinical_scores)
    ? cardSummary.clinical_scores
    : [];
  const concern = clinicalScores.find((s) => {
    if (/barthel/i.test(String(s?.tool_name ?? ""))) return false;
    const risk = String(s?.risk_level ?? "").toLowerCase();
    const trend = String(s?.trend ?? "").toLowerCase();
    return risk === "high" || risk === "very_high" || trend === "declining";
  });
  if (concern) {
    const name = concern.tool_name || "Clinical score";
    const shortName = /waterlow/i.test(name)
      ? "Waterlow"
      : /must|malnutrition/i.test(name)
        ? "MUST"
        : /braden/i.test(name)
          ? "Braden"
          : name;
    const risk = String(concern.risk_level ?? "").toLowerCase();
    const isHigh = risk === "high" || risk === "very_high";
    const declining = String(concern.trend ?? "").toLowerCase() === "declining";
    const badge = isHigh
      ? `${shortName} high`
      : concern.score != null
        ? `${shortName} ${concern.score}`
        : shortName;
    items.push({
      key: "clinical",
      tone: isHigh ? "danger" : "warning",
      label: `${badge}${declining ? " ↓" : ""}`,
      sub: null,
      tab: "observations",
      title: `${name}${concern.score != null ? ` — ${concern.score}` : ""}${
        concern.trend ? ` · ${concern.trend}` : ""
      }`,
    });
  }

  return items;
};

export const isResidentAllGood = (cardSummary) =>
  Boolean(cardSummary?.is_all_good);

/**
 * Left-border RAG accent — reserved for genuine act-now cards (critical) so the
 * rail actually discriminates in a full grid. Amber/green are dropped: routine
 * "due soon" work is carried by the attention chips and "all on track" by its
 * own row, so lighting most cards amber just added noise.
 */
export const getResidentCardAccent = (cardSummary) => {
  if (
    cardSummary?.overall_status === "critical" ||
    cardSummary?.urgency_rank === 1
  ) {
    return "danger";
  }
  return null;
};

/**
 * Person-state meta for the status dot. This is deliberately about *how the
 * resident is doing* (wellbeing + acute clinical picture) — NOT about pending
 * paperwork. Chase-ups (overdue reviews, due-soon, missed rounds) live on the
 * attention chips instead. "unknown" (grey) is honest: we have no recent
 * check-in, so we don't claim green.
 */
export const PERSON_STATE_META = {
  unwell: { status: "unwell", emoji: "🔴", label: "Needs attention" },
  watch: { status: "watch", emoji: "🟡", label: "Keep an eye" },
  well: { status: "well", emoji: "🟢", label: "Well — all on track" },
  unknown: { status: "unknown", emoji: "⚪", label: "No recent check-in" },
};

/**
 * Grey variant for a newly admitted resident: still honestly "unknown" (same
 * dot, same status semantics — no check-in data yet), but "settling in" reads
 * as expected onboarding rather than a lapsed check-in.
 */
const NEWLY_ADMITTED_UNKNOWN_META = {
  ...PERSON_STATE_META.unknown,
  label: "Newly admitted — settling in",
};

/**
 * The status dot: the person's state, from data card_summary already ships.
 * Red = unwell / at acute clinical risk; amber = an early concern to watch;
 * green = eating, drinking, settled with no acute concern; grey = nothing
 * recorded recently, so we can't say (prompts a check-in rather than faking OK).
 */
export const resolvePersonState = (cardSummary) => {
  if (!cardSummary || typeof cardSummary !== "object") {
    return PERSON_STATE_META.unknown;
  }

  const wb = cardSummary.wellbeing ?? {};
  const alerts = cardSummary.ai_alerts ?? {};
  const obs = cardSummary.observations ?? {};
  const clinical = Array.isArray(cardSummary.clinical_scores)
    ? cardSummary.clinical_scores
    : [];
  const barthel = cardSummary.barthel ?? {};

  // Acute clinical picture only — NOT the composite risk.level (that also folds
  // in overdue paperwork and chronic/standing risk, which belong on the chips or
  // the Risk flag, not on a "how are they today" dot). We react to live signals:
  // high/medium AI alerts, an overdue monitoring obs, and active clinical-tool
  // risk or decline (Waterlow, MUST, Barthel trend, etc.).
  const highAlert = (alerts.open_by_priority?.high ?? 0) > 0;
  const medAlert = (alerts.open_by_priority?.medium ?? 0) > 0;
  const overdueObs = (obs.overdue_count ?? 0) > 0; // monitoring gap counts
  let clinicalHigh = false;
  let clinicalDeclining = false;
  clinical.forEach((s) => {
    const rl = String(s?.risk_level ?? "").toLowerCase();
    if (rl === "high" || rl === "very_high") clinicalHigh = true;
    if (String(s?.trend ?? "").toLowerCase() === "declining") {
      clinicalDeclining = true;
    }
  });
  if (String(barthel.trend ?? "").toLowerCase() === "declining") {
    clinicalDeclining = true;
  }
  const clinicalRed = highAlert || overdueObs || clinicalHigh;
  const clinicalAmber = medAlert || clinicalDeclining;

  // Recent wellbeing (mood within the freshness window; meals are already
  // scoped to ~48h and fluids to today by the API).
  const mood = wb.mood;
  const moodAge = mood ? daysSince(mood.recorded_at) : null;
  const moodRecent =
    Boolean(mood?.value) && moodAge != null && moodAge <= STALE_MOOD_DAYS;
  const moodGroupRecent = moodRecent ? moodGroup(mood.value, mood.group) : null;

  const consumed =
    wb.nutrition && wb.nutrition.last_meal_consumed != null
      ? Number(wb.nutrition.last_meal_consumed)
      : null;
  const mealRecent = consumed != null;

  const drinks = Number(wb.hydration?.drinks_today) || 0;
  const ml = Number(wb.hydration?.total_ml_today) || 0;
  const hydrationToday = drinks > 0;
  const hydrationLow = hydrationToday && ml > 0 && ml < LOW_FLUID_ML;

  // Mood by GROUP: anxious / distressed → red, low → amber (flat is neutral).
  const wellRed =
    moodGroupRecent === "distressed" || (mealRecent && consumed === 0);
  const wellAmber =
    moodGroupRecent === "low" ||
    (mealRecent && consumed > 0 && consumed <= POOR_INTAKE_PCT) ||
    hydrationLow;

  // "Known" follows the care-day rhythm rather than a fixed window. Overnight
  // (residents asleep) and before late morning we carry over the most recent
  // check-in, so the grid isn't grey the moment a shift starts. From late
  // morning on we expect a check-in logged *today* — no today's data yet means
  // grey ("no recent check-in"), and cards re-light as staff record through the
  // day. Acute clinical/wellbeing concern above always wins over grey.
  const hour = new Date().getHours();
  const expectTodayData =
    hour >= CHECKIN_EXPECTED_FROM_HOUR && hour < NIGHT_FROM_HOUR;
  const moodToday = moodRecent && daysSince(mood.recorded_at) === 0;
  const mealsToday = Number(wb.nutrition?.meals_today) || 0;
  const hasTodayData = moodToday || mealsToday > 0 || hydrationToday;
  const hasRecentData = moodRecent || mealRecent || hydrationToday;
  const known = expectTodayData ? hasTodayData : hasRecentData;

  if (clinicalRed || wellRed) return PERSON_STATE_META.unwell;
  if (clinicalAmber || wellAmber) return PERSON_STATE_META.watch;
  if (known) return PERSON_STATE_META.well;
  if (cardSummary.onboarding?.is_new) return NEWLY_ADMITTED_UNKNOWN_META;
  return PERSON_STATE_META.unknown;
};
